import { Grid, TextField, TextFieldProps } from '@material-ui/core';
import React, { createRef, Dispatch, MutableRefObject, SetStateAction, useEffect, useState } from 'react';
import { TimeField, TimeFieldProps } from '../../TimeField';
import OverrideInput from '../../OverrideInput';
import {
  generateMergeKeyPlaceholder,
  generateNewValue,
  MergeField,
  MergeFieldFormatKeys,
  parseInputValue,
} from '@fluentsms/merge';
import { useStyles } from '../RichTextMergeFieldEditor';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { get } from 'lodash';
import CurrencyField from '../../CurrencyField';
import { DateField, DateFieldProps } from '../../';
import { PercentageField } from '../../PercentageField';
import NumberMaskedField from '../../NumberMaskedField';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from '@fluentsms/i18n';

interface RichTextEditorMergeFieldSectionProps {
  mergeFields: Map<string, MergeField>;
  setMergeFields: Dispatch<SetStateAction<Map<string, MergeField>>>;
  elements: Map<string, HTMLElement[]>;
  control: Control<FieldValues>;
}

const renderField = (
  dataType: MergeFieldFormatKeys | undefined,
  props: TextFieldProps,
  index: string,
  errorStates: Map<string, string>,
): JSX.Element => {
  switch (dataType) {
    case MergeFieldFormatKeys.CURRENCY: {
      return <CurrencyField {...props} data-testid="override-input-currency-field" />;
    }
    case MergeFieldFormatKeys.DATE: {
      const errorMessage = errorStates.get(index);
      return (
        <DateField
          {...(props as DateFieldProps)}
          PopoverProps={{ disablePortal: true }}
          fullWidth
          id={props.name}
          data-testid="override-input-date-field"
          error={!!errorMessage}
          helperText={errorMessage || ''}
        />
      );
    }
    case MergeFieldFormatKeys.PERCENT: {
      return <PercentageField {...props} data-testid="override-input-percentage-field" />;
    }
    case MergeFieldFormatKeys.NUMBER: {
      return <NumberMaskedField {...props} data-testid="override-input-number-field" />;
    }
    case MergeFieldFormatKeys.TIME: {
      return <TimeField {...(props as TimeFieldProps)} ampm data-testid="override-input-time-field" />;
    }
    default: {
      return <TextField {...props} data-testid="override-input-text-field" />;
    }
  }
};

function RichTextEditorMergeFieldSection(props: RichTextEditorMergeFieldSectionProps): JSX.Element {
  const { mergeFields, setMergeFields, elements, control } = props;
  const classes = useStyles();
  const { t } = useTranslation('documents');
  const [errorStates, setErrorStates] = useState<Map<string, string>>(new Map());

  // Refs to text fields
  const [elRefs, setElRefs] = useState<MutableRefObject<HTMLInputElement>[]>([]);

  useEffect(() => {
    setElRefs((elRefs) =>
      Array(mergeFields.size)
        .fill(null)
        .map((_, i) => elRefs[i] || createRef()),
    );
  }, [mergeFields.size]);

  const handleTextFieldFocus = (mergeField: string) => {
    const elementList = elements.get(mergeField);
    if (!elementList) return;

    elementList.forEach((element: HTMLElement) => {
      element.classList.add(classes.activeField);
    });
  };

  const handleTextFieldBlur = (mergeField: string) => {
    const elementList = elements.get(mergeField);
    if (!elementList) return;

    elementList.forEach((element: HTMLElement) => {
      element.classList.remove(classes.activeField);
    });
  };

  const handleTextFieldChange = (rawNewValue: string, mergeField: string, mergeObject: MergeField, index: string) => {
    const elementList = elements.get(mergeField);
    if (!elementList) return;

    const parsedValue = parseInputValue(rawNewValue, mergeObject.dataType);
    const { dataType, formatKey, textFormat } = mergeObject;
    let newValue = generateNewValue({ value: parsedValue, dataType, formatKey, textFormat });

    // Handle error state calculations
    const newMap = new Map(errorStates);
    switch (mergeObject.dataType) {
      case MergeFieldFormatKeys.DATE: {
        const dateValue = new Date(newValue);
        const minDate = new Date('01/01/1700');
        const maxDate = new Date('01/01/2100');
        if (String(newValue) === 'Invalid Date') {
          setErrorStates(newMap.set(index, 'Invalid Date'));
          newValue = '';
        } else if (dateValue > maxDate) {
          setErrorStates(newMap.set(index, 'Must be before 1/1/2100'));
          newValue = '';
        } else if (dateValue < minDate) {
          setErrorStates(newMap.set(index, 'Must be later than 1/1/1700'));
          newValue = '';
        } else if (errorStates.get(index)) {
          newMap.delete(index);
          setErrorStates(newMap);
        }
      }
    }

    elementList.forEach((element: HTMLElement) => {
      element.innerHTML = newValue || generateMergeKeyPlaceholder(mergeObject.fieldLabel);
      if (!mergeObject.fillInField && !element.classList.contains(classes.overriddenField)) {
        element.classList.add(classes.overriddenField);
      }
    });

    // If merge field is not a fill in field don't set field to overridden
    if (!mergeObject.fillInField && !mergeObject.isOverridden) {
      mergeObject.isOverridden = true;
    }

    mergeObject.fieldValue = newValue;
    setMergeFields((prevMergeFields: Map<string, MergeField>) => {
      const newState = new Map(prevMergeFields);
      newState.set(mergeField, mergeObject);
      return newState;
    });
  };

  const handleRevert = (mergeField: string, mergeObject: MergeField, index: number) => {
    const elementList = elements.get(mergeField);
    if (!elementList) return;

    const newMap = new Map(errorStates);
    newMap.delete(String(index));
    setErrorStates(newMap);

    const { originData: value, dataType, formatKey, textFormat } = mergeObject;
    const formattedValue = generateNewValue({ value, dataType, formatKey, textFormat });

    mergeObject.fieldValue = formattedValue;
    mergeObject.isOverridden = false;

    elementList.forEach((element: HTMLElement) => {
      element.innerHTML = formattedValue || generateMergeKeyPlaceholder(mergeObject.fieldLabel);
      element.classList.remove(classes.overriddenField);
      element.classList.add(classes.activeField);
    });

    setMergeFields((prevMergeFields: Map<string, MergeField>) => {
      const newState = new Map(prevMergeFields);
      newState.set(mergeField, mergeObject);
      return newState;
    });

    // set focus to the text field being reverted
    elRefs[index]?.current.focus();
  };

  return (
    <form>
      <Grid container>
        {Array.from(mergeFields.entries()).map(([mergeField, obj], index) => {
          if (!elements.has(mergeField)) return null;

          return (
            <Grid item xs={12} sm={6} md={4} key={`mergeFields.${index}`}>
              <Controller
                name={`mergeFields.${index}`}
                control={control}
                defaultValue={obj.rawValue ?? ''}
                render={({ field: { ref, value, onChange, onBlur, ...renderProps } }) => (
                  <>
                    {obj.options ? (
                      <Autocomplete
                        data-testid="document-merge-field-autocomplete"
                        {...renderProps}
                        autoComplete
                        value={value}
                        options={obj.options}
                        defaultValue={obj.defaultValue}
                        disableClearable={obj.isOverridden}
                        classes={{ inputRoot: classes.inputRoot }}
                        {...(obj.dataType === MergeFieldFormatKeys.BOOLEAN && {
                          getOptionLabel: (option) =>
                            option === 'true'
                              ? t('fillInFields.checkbox.true', 'Yes/True')
                              : t('fillInFields.checkbox.false', 'No/False'),
                        })}
                        onChange={(_, value) => {
                          onChange(value);
                          handleTextFieldChange(value, mergeField, obj, String(index));
                        }}
                        onBlur={() => handleTextFieldBlur(mergeField)}
                        renderInput={({ ...props }) => {
                          return (
                            <OverrideInput
                              {...props}
                              isOverridden={obj.isOverridden}
                              onRevert={() => {
                                onChange(obj.originData);
                                handleRevert(mergeField, obj, index);
                              }}
                              inputRef={(e) => {
                                ref(e);
                                elRefs[index] = { current: e };
                              }}
                              onFocus={() => handleTextFieldFocus(mergeField)}
                              onBlur={() => handleTextFieldBlur(mergeField)}
                              multiline
                              label={obj.fieldLabel}
                            />
                          );
                        }}
                      />
                    ) : (
                      <OverrideInput
                        {...renderProps}
                        renderInput={(props) => renderField(obj.dataType, props, String(index), errorStates)}
                        value={value ?? ''}
                        sourceValue={value ?? ''}
                        inputProps={{ 'data-testid': 'rich-text-editor-merge-field-text-field-input' }}
                        label={obj.fieldLabel}
                        fullWidth
                        onFocus={() => handleTextFieldFocus(mergeField)}
                        onBlur={() => handleTextFieldBlur(mergeField)}
                        onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                          const value = get(event, 'target.value', event);
                          onChange(value);
                          handleTextFieldChange(value, mergeField, obj, String(index));
                        }}
                        isOverridden={obj.isOverridden}
                        onRevert={() => {
                          onChange(obj.originData);
                          handleRevert(mergeField, obj, index);
                        }}
                        inputRef={(e) => {
                          ref(e);
                          elRefs[index] = { current: e };
                        }}
                      />
                    )}
                  </>
                )}
              />
            </Grid>
          );
        })}
      </Grid>
    </form>
  );
}

export default RichTextEditorMergeFieldSection;
